import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Pencil, Trash2, Plus } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { api, get, formatApiErrorDetail } from "@/lib/api";

const IMAGES = ["p5366406", "p9716228", "p7642037", "p7414960", "p7294672", "p9936969", "u1", "u2", "u3"];
const BLANK = { title: "", category: "Guides", excerpt: "", body: "", image: "p9716228", published: true };

function ArticleDialog({ article, onClose, onSaved }) {
  const [f, setF] = useState(article ? { ...BLANK, ...article } : BLANK);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async () => {
    const body = { title: f.title, category: f.category, excerpt: f.excerpt, body: f.body, image: f.image, published: f.published };
    try {
      article ? await api.put(`/admin/articles/${article.id}`, body) : await api.post("/admin/articles", body);
      toast.success("Article saved"); onSaved();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-white max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="admin-article-dialog">
        <DialogHeader><DialogTitle>{article ? "Edit article" : "New article"}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5"><Label htmlFor="ar-title">Title</Label><Input id="ar-title" value={f.title} onChange={set("title")} data-testid="admin-article-title" /></div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-1.5"><Label htmlFor="ar-cat">Category</Label><Input id="ar-cat" value={f.category} onChange={set("category")} data-testid="admin-article-category" /></div>
            <label className="flex items-center gap-3 pt-6 text-sm"><Switch checked={f.published} onCheckedChange={(v) => setF({ ...f, published: v })} data-testid="admin-article-published" />Published</label>
          </div>
          <div className="space-y-1.5"><Label htmlFor="ar-ex">Excerpt</Label><Textarea id="ar-ex" rows={2} value={f.excerpt} onChange={set("excerpt")} data-testid="admin-article-excerpt" /></div>
          <div className="space-y-1.5"><Label htmlFor="ar-body">Body (use "## " for headings, blank line between paragraphs, "- " for lists)</Label><Textarea id="ar-body" rows={10} value={f.body} onChange={set("body")} data-testid="admin-article-body" /></div>
          <fieldset><legend className="text-sm font-medium mb-2">Cover image</legend>
            <div className="grid grid-cols-6 gap-2">{IMAGES.map((i) => (
              <button type="button" key={i} onClick={() => setF({ ...f, image: i })} aria-pressed={f.image === i} aria-label={`Image ${i}`} data-testid={`admin-article-image-${i}`}
                className={`rounded-lg overflow-hidden border-2 ${f.image === i ? "border-[#03A896]" : "border-transparent"}`}><img src={`/images/${i}.jpg`} alt="" className="h-14 w-full object-cover" /></button>
            ))}</div></fieldset>
        </div>
        <button onClick={save} disabled={!f.title} className="mt-4 rounded-full bg-[#0B2150] text-white px-6 py-2.5 font-semibold hover:bg-[#03A896] disabled:opacity-50" data-testid="admin-article-save">Save article</button>
      </DialogContent>
    </Dialog>
  );
}

export const AdminArticles = () => {
  const qc = useQueryClient();
  const [edit, setEdit] = useState(undefined);
  const { data = [] } = useQuery({ queryKey: ["admin-articles"], queryFn: () => get("/admin/articles") });
  const refresh = () => { ["admin-articles", "articles", "admin-stats"].forEach((k) => qc.invalidateQueries({ queryKey: [k] })); };
  const del = async (a) => { if (window.confirm(`Delete "${a.title}"?`)) { await api.delete(`/admin/articles/${a.id}`); toast.success("Deleted"); refresh(); } };
  return (
    <div data-testid="admin-articles">
      <button onClick={() => setEdit(null)} className="mb-4 inline-flex items-center gap-2 rounded-full bg-[#03A896] text-white px-4 py-2 text-sm font-semibold hover:bg-[#0B2150]" data-testid="admin-article-new-btn"><Plus className="h-4 w-4" />New article</button>
      <div className="space-y-2">
        {data.map((a) => (
          <div key={a.id} className="flex items-center gap-4 rounded-2xl border border-[#E2E8F0] bg-white p-3" data-testid={`admin-article-row-${a.id}`}>
            <img src={`/images/${a.image}.jpg`} alt="" className="h-14 w-20 object-cover rounded-lg" />
            <div className="flex-1 min-w-0">
              <Link to={`/articles/${a.slug}`} className="font-medium text-[#0B2150] hover:underline line-clamp-1">{a.title}</Link>
              <p className="text-xs text-slate-500">{a.category} · {a.published ? "Published" : "Draft"}</p>
            </div>
            <button onClick={() => setEdit(a)} aria-label={`Edit ${a.title}`} className="p-2 rounded-full hover:bg-slate-100" data-testid={`admin-article-edit-${a.id}`}><Pencil className="h-4 w-4" /></button>
            <button onClick={() => del(a)} aria-label={`Delete ${a.title}`} className="p-2 rounded-full hover:bg-red-50 text-red-700" data-testid={`admin-article-delete-${a.id}`}><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
      </div>
      {edit !== undefined && <ArticleDialog article={edit} onClose={() => setEdit(undefined)} onSaved={() => { setEdit(undefined); refresh(); }} />}
    </div>
  );
};
